import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { useAppSelector } from 'src/store'
import sanitizeHtml from 'sanitize-html'

// Selectors
import { selectChannel } from 'src/store/domain/chat/channel/selector'

//img
import defaultChat from 'src/assets/images/members/chat/default-doctor.png'
import LazyLoadImage from 'src/components/common/LazyLoadImage'
import chatBotAvt from 'src/assets/images/members/chat/chat-bot.svg'
import { MESSAGE_TYPE } from 'src/containers/Chat/constants'
import ButtonMessage from './ButtonMessage'
import { URL_PATTERN } from 'src/constants/app'
import PlanMessage from './PlanMessage'
import { usePlanMessageData } from 'src/hook/usePlanMessageData'

type Props = {
  item: {
    message_content: string
    created_at: string
    attachment_image_url: string
    is_from_bot: boolean
    type_message: number
  }
}

const TheirMessage = (props: Props) => {
  const { item } = props
  const { message_content, created_at, attachment_image_url, type_message } =
    item

  const channel = useAppSelector(selectChannel)
  const manager = channel?.managers?.[0]
  const info = useMemo(() => {
    const imageUrl = item.is_from_bot ? chatBotAvt : manager?.avatar_url
    return {
      imageUrl,
    }
  }, [manager])

  const { planContents, planImportants, isLoading } = usePlanMessageData(
    type_message,
    message_content,
  )

  const messageTime = useMemo(() => {
    moment.locale('ja')
    let startDate = moment(created_at).startOf('day')
    let time = moment(created_at)
    if (moment().diff(startDate, 'days') == 0) {
      return time.format('HH:mm')
    }
    return time.format('M/DD HH:mm')
  }, [created_at])

  const isValidLink = (content) => {
    const urlPattern = new RegExp(
      '^(https?:\\/\\/)?' +
        "((([a-zA-Z0-9$_.+!*'(),;?&=-]|[a-zA-Z0-9-])+\\.)*[a-zA-Z0-9]{2,63}\\.[a-zA-Z]{2,6})" +
        "(\\/[-a-zA-Z0-9$_.+!*'(),;?&=-]*)?" +
        "(\\?[a-zA-Z0-9$_.+!*'(),;?&=-]*)?",
      'i',
    )
    return urlPattern.test(content)
  }

  const isMessageBtn = useMemo(
    () => type_message === MESSAGE_TYPE.BUTTON,
    [type_message],
  )

  const convertToHtml = useCallback((content) => {
    const pattern = new RegExp(URL_PATTERN, 'g')

    return sanitizeHtml(
      content.replace(
        pattern,
        '<a href="$1" rel="noopener noreferrer" target="_blank">$1</a>',
      ),
    )
  }, [])

  return (
    <Wrapper>
      <div className="their-message">
        <img
          src={info.imageUrl ? info.imageUrl : defaultChat}
          alt="their-avatar"
          className="their-message__avatar"
        />
        {attachment_image_url && (
          <div>
            <LazyLoadImage
              height="100px"
              width="30%"
              className="w-30 ml-10"
              imgSrc={attachment_image_url}
            />
            <span className="their-message__time">{messageTime}</span>
          </div>
        )}
        {message_content && (
          <div className="their-message__message-content">
            <div className="their-message__message">
              {type_message === MESSAGE_TYPE.PLAN ? (
                <div className="plan-item-wrap">
                  {isLoading ? (
                    <div className="typing-indicator">
                      <span className="typing-dot"></span>
                      <span className="typing-dot"></span>
                      <span className="typing-dot"></span>
                    </div>
                  ) : (
                    <div>
                      {planImportants.length > 0 && (
                        <div className="plan-section important">
                          <h4 className="plan-recommended-title">
                            赤枠のオススメのお薬は、医師が診察時あなたに合うと考えて選んだオススメの薬になります
                          </h4>
                          {planImportants.map((plan, idx) => (
                            <PlanMessage
                              key={plan.id || `important-${idx}`}
                              content={plan}
                              isImportant={true}
                            />
                          ))}
                        </div>
                      )}
                      {planContents.map((plan, idx) => (
                        <PlanMessage
                          key={plan.id || `plan-${idx}`}
                          content={plan}
                          isImportant={false}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ) : isValidLink(message_content) ? (
                <a href={message_content} rel="noopener noreferrer">
                  {message_content}
                </a>
              ) : !isMessageBtn ? (
                <span
                  dangerouslySetInnerHTML={{
                    __html: convertToHtml(message_content),
                  }}
                ></span>
              ) : (
                <ButtonMessage item={item} />
              )}
            </div>
            <span className="their-message__time">{messageTime}</span>
          </div>
        )}
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  .their-message {
    margin-bottom: 20px;
    display: flex;
    justify-content: flex-start;

    &__avatar {
      width: 40px;
      height: 40px;
      box-shadow: 0 1px 3px 0 rgb(0 0 0 / 10%), 0 1px 2px 0 rgb(0 0 0 / 6%);
      border-radius: 50%;
      object-fit: cover;
    }

    &__message {
      margin-left: 10px;
      background: white;
      padding: 10px 8px;
      border-radius: 10px;
      max-width: 66%;
      word-break: break-word;
      font-size: 14px;
      text-align: left;
      white-space: pre-line;
      border: 1px solid #000;
      width: fit-content;

      &-content {
        width: 100%;
      }

      a {
        color: #1890ff;
      }

      a:hover {
        color: #40a9ff;
      }

      .plan-item-wrap {
        max-height: 500px;
        overflow-y: auto;

        &::-webkit-scrollbar {
          width: 0px;
        }
      }
    }

    &__time {
      align-items: flex-end;
      display: flex;
      font-size: 12px;
      color: #989898;
      padding-left: 8px;
    }

    .plan-recommended-title {
      font-size: 14px;
      padding: 10px 15px;
      color: #c20505;
    }

    .typing-indicator {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 4px;
      height: 24px;
      padding: 8px 12px;
      background: #f0f0f0;
      border-radius: 20px;
      width: fit-content;
    }

    .typing-dot {
      width: 6px;
      height: 6px;
      background-color: #888;
      border-radius: 50%;
      animation: blink 1.4s infinite both;
    }

    .typing-dot:nth-child(2) {
      animation-delay: 0.2s;
    }
    .typing-dot:nth-child(3) {
      animation-delay: 0.4s;
    }

    @keyframes blink {
      0% {
        opacity: 0.2;
      }
      20% {
        opacity: 1;
      }
      100% {
        opacity: 0.2;
      }
    }
  }
`

export default TheirMessage
